let lastTraceId: string | null = null;
let lastSentryEventId: string | null = null;

export function setLastTraceId(traceId: string | null | undefined): void {
  const v = traceId?.trim();
  lastTraceId = v || null;
}

export function getLastTraceId(): string | null {
  return lastTraceId;
}

export function setLastSentryEventId(eventId: string | null | undefined): void {
  const v = eventId?.trim();
  lastSentryEventId = v || null;
}

export function getLastSentryEventId(): string | null {
  return lastSentryEventId;
}

export type ReportIssueContext = {
  url?: string;
  userAgent?: string;
  traceId?: string;
  sentryEventId?: string;
  surface?: string;
  route?: string;
  componentStack?: string;
};

export function buildReportIssueContext(extra?: Partial<ReportIssueContext>): ReportIssueContext {
  const out: ReportIssueContext = { ...extra };
  if (typeof window !== 'undefined') {
    out.url = out.url ?? window.location.href;
    out.userAgent = out.userAgent ?? window.navigator.userAgent;
  }
  const traceId = getLastTraceId();
  const sentryEventId = getLastSentryEventId();
  if (traceId) out.traceId = traceId;
  if (sentryEventId) out.sentryEventId = sentryEventId;
  return out;
}

/** GlitchTip / Sentry ingest host from a DSN (`http://key@host/project`). */
export function sentryIngestOriginFromDsn(dsn?: string | null): string | undefined {
  if (!dsn?.trim()) return undefined;
  const m = dsn.trim().match(/^(https?):\/\/[^@]+@([^/?#]+)/);
  return m ? `${m[1]}://${m[2]}` : undefined;
}

/** Same-origin tunnel path for browser SDK (proxied to ingest by API). */
export function sentryBrowserTunnelPath(apiBase?: string | null): string | undefined {
  const base = apiBase?.trim().replace(/\/$/, '');
  if (base) return `${base}/observability/sentry-tunnel`;
  return '/api/observability/sentry-tunnel';
}

export function parseApiErrorTraceId(body: unknown, response?: Response): string | null {
  if (body && typeof body === 'object' && 'traceId' in body) {
    const traceId = (body as { traceId?: unknown }).traceId;
    if (typeof traceId === 'string' && traceId.trim()) return traceId.trim();
  }
  const header = response?.headers.get('x-request-id');
  if (header?.trim()) return header.trim();
  return null;
}

/** French copy for Sentry/GlitchTip `showReportDialog` (User Reports tab). */
export const SENTRY_USER_REPORT_DIALOG_FR = {
  lang: 'fr',
  title: 'Une erreur est survenue',
  subtitle: 'Notre équipe a été notifiée.',
  subtitle2: 'Décrivez ce qui s’est passé pour nous aider.',
  labelName: 'Nom',
  labelEmail: 'Email',
  labelComments: 'Que s’est-il passé ?',
  labelClose: 'Fermer',
  labelSubmit: 'Envoyer',
  errorGeneric: 'Envoi impossible. Réessayez.',
  errorFormEntry: 'Corrigez les champs invalides.',
  successMessage: 'Merci, votre message a été envoyé.',
} as const;
